import { useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { cn } from "@pontimed/ui";
import { m } from "framer-motion";
import { Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";

const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

interface BloodTypeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentValue: string | null;
  onSave: (bloodType: string | null) => Promise<void>;
}

export function BloodTypeModal({ open, onOpenChange, currentValue, onSave }: BloodTypeModalProps) {
  const { t } = useLingui();
  const [selected, setSelected] = useState<string | null>(currentValue);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(selected);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveSheet
      open={open}
      onClose={() => onOpenChange(false)}
      title={<Trans>Edit blood type</Trans>}
      description={<Trans>Select your blood type. Tap again to clear.</Trans>}
      width="sm"
      ariaLabel={t`Edit blood type`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="min-h-[44px]">
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleSave} disabled={saving} className="min-h-[44px]">
            {saving ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-4 gap-2">
        {BLOOD_TYPES.map((bt, i) => (
          <m.button
            key={bt}
            type="button"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: i * 0.03 }}
            onClick={() => setSelected(selected === bt ? null : bt)}
            className={cn(
              "relative flex min-h-[48px] items-center justify-center rounded-xl border text-sm font-semibold transition-all",
              selected === bt
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-foreground hover:bg-muted",
            )}
          >
            {bt}
            {selected === bt && (
              <m.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="bg-primary absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full"
              >
                <Check className="size-2.5 text-white" />
              </m.div>
            )}
          </m.button>
        ))}
      </div>
    </ResponsiveSheet>
  );
}
